import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { sumBy } from 'lodash';
import moment from 'moment';

export interface LineChartTimelineColumnsByOptions {
  lastSevenDays: number;
  lastThirtyDays: number;
  lastNinetyDays: number;
  lastSixMonths: number;
  lastTwelveMonths: number;
}

export interface LineChartResultItem {
  name: string;
  series: LineChartSeries[];
}

export interface LineChartSeries {
  name: string;
  formattedName?: string;
  initialDate: string;
  finalDate?: string;
  totalCount?: number;
  value: number;
}

export interface LineChartTimelineOptions {
  lastSevenDays: string;
  lastThirtyDays: string;
  lastNinetyDays: string;
  lastSixMonths: string;
  lastTwelveMonths: string;
}

@Injectable({
  providedIn: 'root'
})
export class LineMapperService {

  constructor(
    private translate: TranslateService
  ) { }

  public readonly timelineOptions: LineChartTimelineOptions = {
    lastSevenDays: moment().subtract(6, 'days').startOf('day').utc().toISOString(),
    lastThirtyDays: moment().subtract(29, 'days').startOf('day').utc().toISOString(),
    lastNinetyDays: moment().subtract(89, 'days').startOf('day').utc().toISOString(),
    lastSixMonths: moment().subtract(5, 'months').startOf('day').utc().toISOString(),
    lastTwelveMonths: moment().subtract(11, 'months').startOf('day').utc().toISOString(),
  };

  public readonly columnsByTimelineOption: LineChartTimelineColumnsByOptions = {
    lastSevenDays: 7,
    lastThirtyDays: 10,
    lastNinetyDays: 9,
    lastSixMonths: 6,
    lastTwelveMonths: 12,
  };

  public toLineChartFormat(accountRatingsAverageByTimeline, timelineOption): LineChartResultItem[] {
    if (timelineOption === 'lastSevenDays') {
      return this.toTimelineLastSevenDays(accountRatingsAverageByTimeline, timelineOption);
    }

    if (timelineOption === 'lastThirtyDays' || timelineOption === 'lastNinetyDays') {
      const range = timelineOption === 'lastThirtyDays' ? 3 : 10;
      return this.toTimelineLastWithRangedDays(accountRatingsAverageByTimeline, range, timelineOption);
    }

    return this.toTimelineLastMonths(accountRatingsAverageByTimeline, timelineOption);
  }

  private toTimelineLastSevenDays(data, timelineOption): LineChartResultItem[] {
    const timeline = [];

    for (let i = 0; i < this.columnsByTimelineOption[timelineOption]; i++) {
      const date = moment().subtract(i, 'days');
      const formattedDay = moment(date).format('dddd').charAt(0).toUpperCase() + moment(date).format('dddd').slice(1);
      const hasAverageProperty = data[i].hasOwnProperty('average');

      if (hasAverageProperty) {
        timeline.push({
          name: date.format('ddd DD/MM'),
          formattedName: formattedDay,
          initialDate: moment(date).format(),
          value: data[i].average,
          totalCount: data[i].totalCount
        });
      } else {
        timeline.push({
          name: date.format('ddd DD/MM'),
          formattedName: formattedDay,
          initialDate: moment(date).format(),
          value: data[i].totalCount
        });
      }
    }

    return [{
      name: this.translate.instant('RATINGS.CARDS.GRAPHIC.AVERAGE.LABEL'),
      series: timeline.reverse()
    }];
  }

  private toTimelineLastWithRangedDays(data, range, timelineOption): LineChartResultItem[] {
    const timeline = [];
    const firstDayOfSheet = this.timelineOptions[timelineOption];

    for (let i = 0; i < this.columnsByTimelineOption[timelineOption]; i++) {
      const firstDay = moment(firstDayOfSheet).add(i * range, 'days').format('YYYY-MM-DD');
      const lastDay = moment(firstDay).add(range - 1, 'days').format('YYYY-MM-DD');
      const hasAverageProperty = data[i].hasOwnProperty('average');
      const items = data.filter(({ completedAt }) => {
        return moment(completedAt).isSameOrAfter(firstDay) && moment(completedAt).isSameOrBefore(lastDay);
      });
      const totalCount = items.reduce((accumulator, item) => {
        return accumulator + item.totalCount;
      }, 0);

      if (hasAverageProperty) {
        const averageValue = this.calcAverageOfItems(items);

        timeline.push({
          name: `${moment(firstDay).format('DD/MM')} - ${moment(lastDay).format('DD/MM')}`,
          formattedName: `${moment(firstDay).format('DD/MM')} - ${moment(lastDay).format('DD/MM')}`,
          initialDate: moment(firstDay).format(),
          finalDate: moment(lastDay).format(),
          totalCount: totalCount,
          value: averageValue,
        });
      } else {
        timeline.push({
          name: `${moment(firstDay).format('DD/MM')} - ${moment(lastDay).format('DD/MM')}`,
          formattedName: `${moment(firstDay).format('DD/MM')} - ${moment(lastDay).format('DD/MM')}`,
          initialDate: moment(firstDay).format(),
          finalDate: moment(lastDay).format(),
          value: totalCount,
        });
      }
    }

    return [{
      name: this.translate.instant('RATINGS.CARDS.GRAPHIC.AVERAGE.LABEL'),
      series: timeline
    }];
  }

  private toTimelineLastMonths(data, timelineOption): LineChartResultItem[] {
    const firstDayOfSheet = this.timelineOptions[timelineOption];
    const timeline = [];

    for (let i = 0; i < this.columnsByTimelineOption[timelineOption]; i++) {
      const date = moment(firstDayOfSheet).add(i, 'months').format('YYYY-MM-DD');
      const formattedDate = moment(date).format('MMM').charAt(0).toUpperCase() + moment(date).format('MMM').slice(1);
      const formattedMonth = moment(date).format('MMMM').charAt(0).toUpperCase() + moment(date).format('MMMM').slice(1);
      const hasAverageProperty = data[i].hasOwnProperty('average');

      if (hasAverageProperty) {
        timeline.push({
          name: formattedDate,
          formattedName: formattedMonth,
          initialDate: moment(date).format(),
          totalCount: data[i].totalCount,
          value: data[i].average
        });
      } else {
        timeline.push({
          name: formattedDate,
          formattedName: formattedMonth,
          initialDate: moment(date).format(),
          value: data[i].totalCount
        });
      }
    }

    return [{
      name: this.translate.instant('RATINGS.CARDS.GRAPHIC.AVERAGE.LABEL'),
      series: timeline
    }];
  }

  public calcAverageOfItems(items): number{

    const sumOfCount = sumBy(items, 'totalCount');

    items.forEach((item) => {
      item.averageSum = item.totalCount * item.average;
    });

    const sumOfAverageOfAllItems = sumBy(items, 'averageSum');

    if (sumOfAverageOfAllItems !== 0 && sumOfCount !== 0){
      return sumOfAverageOfAllItems / sumOfCount;
    }

    return 0;
  }
}
